<template>
    <v-dialog
        v-model="showModal"
        max-width="400"
        persistent>
        <v-card class="tw-rounded-lg">
            <v-card-title class="tw-py-6 tw-text-center">
                <div class="tw-flex tw-flex-col tw-items-center tw-gap-4">
                    <v-img
                        class="tw-size-12"
                        src="@/assets/logo.svg"></v-img>
                    <span class="tw-text-lg tw-font-semibold">{{ $t('pwaInstall.iosModal.title') }}</span>
                </div>
            </v-card-title>

            <v-card-text class="tw-px-6 tw-pb-6">
                <div class="tw-space-y-4">
                    <div class="tw-flex tw-items-start tw-gap-3">
                        <span class="tw-flex tw-size-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-text-sm tw-font-semibold tw-text-white">1</span>
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <template v-if="isSafariOnIOS()">
                                <span>{{ $t('pwaInstall.iosModal.step1') }}</span>
                                <SafariShare class="tw-text-blue-500 tw-size-5" />
                            </template>
                            <template v-else-if="isIOSDevice()">
                                <!-- to install the app on iOS you need to use Safari -->
                                <span>{{ $t('pwaInstall.iosModal.step1UseSafari') }}</span>
                            </template>
                            <template v-else>
                                <span>{{ $t('pwaInstall.iosModal.step1Alternative') }}</span>
                                <ThreeDots class="tw-text-gray-500 tw-size-5" />
                            </template>
                        </div>
                    </div>

                    <div class="tw-flex tw-items-start tw-gap-3">
                        <span class="tw-flex tw-size-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-text-sm tw-font-semibold tw-text-white">2</span>
                        <span>{{ isSafariOnIOS() || !isIOSDevice() ? $t('pwaInstall.iosModal.step2') : $t('pwaInstall.iosModal.step2UseSafari') }}</span>
                    </div>

                    <div
                        v-if="isSafariOnIOS() || !isIOSDevice()"
                        class="tw-flex tw-items-start tw-gap-3">
                        <span class="tw-flex tw-size-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-text-sm tw-font-semibold tw-text-white">3</span>
                        <span>{{ $t('pwaInstall.iosModal.step3') }}</span>
                    </div>

                    <div
                        v-if="isIOSDevice() && !isSafariOnIOS()"
                        class="tw-mt-2 tw-flex tw-items-start tw-justify-center">
                        <Button @click="copyUrl">
                            {{ $t('shared.copy') }}
                        </Button>
                        <v-snackbar
                            v-model="snackbar"
                            class="tw-rounded-lg tw-text-dark"
                            color="white"
                            :timeout="2000">
                            {{ text }}
                        </v-snackbar>
                    </div>
                </div>
            </v-card-text>

            <v-card-actions class="tw-px-6 tw-pb-6">
                <v-spacer />
                <Button @click="closeModal">
                    {{ $t('pwaInstall.iosModal.close') }}
                </Button>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
    import Button from './shared/Button.vue';
    import SafariShare from './icons/SafariShare.vue';
    import { usePWAInstall } from '@/composables/usePWAInstall';
    import { useI18n } from 'vue-i18n';

    const { t } = useI18n();
    const { isSafariOnIOS, isIOSDevice } = usePWAInstall();
    interface Props {
        modelValue: boolean;
    }

    interface Emits {
        'update:modelValue': [value: boolean];
    }

    const props = defineProps<Props>();
    const emit = defineEmits<Emits>();

    const snackbar = ref(false);
    const text = ref('');

    const showModal = computed({
        get: () => props.modelValue,
        set: (value: boolean) => emit('update:modelValue', value),
    });

    const copyUrl = () => {
        const url = window.location.href;
        navigator.clipboard
            .writeText(url)
            .then(() => {
                snackbar.value = true;
                text.value = t('shared.copiedToClipboard');
            })
            .catch((err) => {
                console.error('Failed to copy URL: ', err);
                snackbar.value = true;
                text.value = t('shared.copyFailed');
            });
    };

    const closeModal = () => {
        showModal.value = false;
    };
</script>

<style scoped></style>
